/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { LoggerStore } from "../../logger/logger-store"
import type { Result } from "../../network/http-client"
import { makeGetRequest, makePostRequest } from "../../network/http-client"

import type { UserListStore } from "./user-list-store"
import type { UserInvite, UserList } from "./user-types"
import { InviteStatus } from "./user-types"

enum Endpoint {
    user = "/api/private/user",
    invite = "/api/private/user/invite"
}

export class UserListService {
    constructor(
        private readonly store: UserListStore,
        private readonly logger: LoggerStore
    ) {}

    async getUsers(orgId: number): Promise<Result<UserList[]>> {
        this.store.setLoadingUsers(true)
        const result = await this.fetchUsers(orgId)

        if (result.success && result.data) {
            this.store.setUsers(result.data)
        }

        if (!result.success) {
            this.logger.logApiError("Get organization users failed", result.apiError, result.error)
        }
        this.store.setLoadingUsers(false)
        return result
    }

    async inviteExternalUser(username: string, applicationTypeIds: number[]): Promise<InviteStatus> {
        const res = await makePostRequest<UserInvite, void>(`${Endpoint.invite}`, {}, { username, applicationTypeIds })
        if (res.success) {
            this.logger.logOk("External user invitation was sent")
            return InviteStatus.Success
        }

        this.logger.logApiError("Invite external user failed", res.apiError, res.error)

        // Best effort: the API uses HTTP 400 for both "user not found" and other
        // validation failures, so we can't distinguish them here.
        return res.apiError?.status === 400 ? InviteStatus.UserNotFound : InviteStatus.Error
    }

    private async fetchUsers(orgId: number): Promise<Result<UserList[]>> {
        const params = orgId ? { organizationId: `${orgId}` } : null
        return makeGetRequest<UserList[]>(Endpoint.user, params)
    }
}
